/**
 * Builds HitCompactViewProps for the static SRP shell.
 * Same shape as interactive mode so we use the same presentational component (HitCompactView).
 * All data from hit + page data; no context or handlers.
 */
import { buildStaticJobUrl } from '../../../utils/routing/static-job-url';
import { getBrandColorStyles, TYOMARKKINATORI_PURPLE } from '../../../components/Hit/utils/brandColorUtils';
import { getLocationDisplayWithCount, getSinglePrioritizedLocation } from '../../../components/location/utils/locationUtils';
import type { HitCompactViewProps } from '../../../components/Hit/HitCompactViewProps';
import type { StaticSRPData } from '../../../types/srp-static';
import type { Locale } from '../../../utils/routing/generate-url';
import type { DisplayJob } from '../../../types';

export function buildHitCompactViewPropsFromStatic(
  hit: Record<string, unknown>,
  index: number,
  data: StaticSRPData,
  locale: Locale,
  pathname: string
): HitCompactViewProps {
  const jobId = String(hit.objectID ?? hit.id ?? '');
  const companyId = String(hit.companyId ?? '');
  const isTyomarkkinatori = Boolean((hit as any).isTyomarkkinatori);
  const rawBrandColour = typeof hit.brandColour === 'string' ? hit.brandColour : undefined;
  const brandColour = isTyomarkkinatori ? TYOMARKKINATORI_PURPLE : rawBrandColour;
  const brandStyles = getBrandColorStyles(brandColour, isTyomarkkinatori);

  const jobUrl = buildStaticJobUrl({
    hit: {
      objectID: jobId,
      normalisedTitle: String(hit.normalisedTitle ?? hit.title ?? ''),
      title: String(hit.title ?? ''),
      companyId,
      location: hit.location,
      remoteness: String(hit.remoteness ?? ''),
    },
    locale,
    currentPathname: pathname,
    query: data.initialQuery ?? '',
    filters: data.initialFilters ?? {},
    page: data.initialPage ?? 0,
    indexName: data.indexName ?? `${locale}_search`,
  });

  const hitAsDisplayJob = hit as unknown as DisplayJob;
  const desktopLocationData = getLocationDisplayWithCount(hitAsDisplayJob);
  const singleLocation = getSinglePrioritizedLocation(hitAsDisplayJob, []);

  const salaryLabel = typeof (hit as any)._salaryLabel === 'string' ? (hit as any)._salaryLabel : '';
  const lastSeenLabel = typeof (hit as any)._lastSeenLabel === 'string' ? (hit as any)._lastSeenLabel : null;

  const rawTitleSubtitle =
    hit.title && hit.title !== hit.normalisedTitle ? String(hit.title) : undefined;

  // Use companyId so static matches interactive: interactive uses CompanyName(name={hit.companyId}).
  // This ensures the company form (e.g. Oy, Ab) is shown the same in static and hydrated view.
  const companyNameDisplay = companyId || undefined;

  const savedIds = data.initialSavedJobIds ?? [];
  const appliedIds = data.initialAppliedJobIds ?? [];
  const viewedIds = data.initialViewedJobIds ?? [];
  const isSaved = savedIds.includes(jobId);
  const isApplied = appliedIds.includes(jobId);
  const isViewed = viewedIds.includes(jobId);
  const currentApplicationState = (data.initialApplicationStateByJobId?.[jobId] ?? null) as import('../../../redux/features/jobs/jobsSlice').ApplicationState | null;

  return {
    articleId: `job-${jobId}`,
    titleId: `job-title-${jobId}`,
    jobTitle: String(hit.normalisedTitle ?? hit.title ?? ''),
    jobUrl,
    companyId,
    companyNameDisplay,
    isTyomarkkinatori,
    index,
    leftBorderStyle: brandStyles.leftBorderStyle,
    brandColour,
    useBrandColour: brandStyles.useBrandColour,
    salaryLabel,
    lastSeenLabel,
    rawTitleSubtitle,
    locale,
    desktopLocationData,
    singleLocation,
    remoteness: String(hit.remoteness ?? '').trim(),
    jobType: String(hit.jobType ?? '').trim(),
    normalisedRoleLevel: String((hit as any).normalisedRoleLevel ?? '').trim(),
    requiredLanguages: Array.isArray((hit as any).requiredLanguages) ? (hit as any).requiredLanguages : [],
    matchScore: null,
    matchScoreData: null,
    postedDate: (hit as any).postedDate,
    applicationDeadline: (hit as any).applicationDeadline,
    isSaved,
    isApplied,
    isViewed,
    currentApplicationState,
    // no interactive – read-only
  };
}
